import React, { useCallback, useRef, useState } from 'react'
import { Button, Tooltip, message } from 'antd'
import InboxOutlined from '@ant-design/icons/InboxOutlined'
import FileTextOutlined from '@ant-design/icons/FileTextOutlined'
import DeleteOutlined from '@ant-design/icons/DeleteOutlined'

import styles from '../PlanManagementV3.module.scss'

const DEFAULT_MAX_FILE_SIZE = 60 * 1024 * 1024
const DEFAULT_ACCEPT = '.csv,text/csv'

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export interface CsvFileDropzoneProps {
  /** Currently selected file, or null when none is selected. */
  file: File | null
  /** Called with the validated file once the user picks or drops one. */
  onFileSelect: (file: File) => void
  onRemoveFile: () => void
  disabled?: boolean
  /** Tooltip shown over the dropzone while disabled. */
  disabledHint?: string
  maxFileSizeBytes?: number
  accept?: string
  ariaLabel?: string
}

export default function CsvFileDropzone({
  file,
  onFileSelect,
  onRemoveFile,
  disabled = false,
  disabledHint,
  maxFileSizeBytes = DEFAULT_MAX_FILE_SIZE,
  accept = DEFAULT_ACCEPT,
  ariaLabel = 'Upload CSV file',
}: CsvFileDropzoneProps) {
  const [isDragOver, setIsDragOver] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const validateAndSelectFile = useCallback(
    (candidate: File) => {
      if (candidate.type !== 'text/csv' && !candidate.name.endsWith('.csv')) {
        message.error('Only CSV files are allowed.')
        return
      }
      if (candidate.size > maxFileSizeBytes) {
        message.error(
          `File exceeds the ${formatFileSize(maxFileSizeBytes)} limit.`
        )
        return
      }
      onFileSelect(candidate)
    },
    [maxFileSizeBytes, onFileSelect]
  )

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const candidate = e.target.files?.[0]
    e.target.value = ''
    if (candidate) validateAndSelectFile(candidate)
  }

  const handleRemoveFile = () => {
    onRemoveFile()
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleDragOver = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault()
      if (!disabled) setIsDragOver(true)
    },
    [disabled]
  )

  const handleDragLeave = useCallback(() => {
    setIsDragOver(false)
  }, [])

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault()
      setIsDragOver(false)
      if (disabled) return
      const candidate = e.dataTransfer.files?.[0]
      if (candidate) validateAndSelectFile(candidate)
    },
    [disabled, validateAndSelectFile]
  )

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        style={{ display: 'none' }}
        onChange={handleFileChange}
        disabled={disabled}
        aria-label={ariaLabel}
      />

      {file ? (
        <div className={styles.csvFilePreview}>
          <FileTextOutlined className={styles.csvFileIcon} />
          <span className={styles.csvFilePreviewMeta}>
            <span className={styles.csvFileName}>{file.name}</span>
            <span className={styles.csvFileSize}>
              {formatFileSize(file.size)}
            </span>
          </span>
          <Button
            type="text"
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={handleRemoveFile}
            aria-label="Remove file"
          />
        </div>
      ) : (
        <Tooltip title={disabled ? disabledHint : undefined}>
          <div
            className={`${styles.csvDropZone} ${isDragOver ? styles.csvDropZoneActive : ''} ${disabled ? styles.csvDropZoneDisabled : ''}`}
            onClick={() => !disabled && fileInputRef.current?.click()}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            role="button"
            tabIndex={disabled ? -1 : 0}
            aria-label={ariaLabel}
            onKeyDown={(e) => {
              if ((e.key === 'Enter' || e.key === ' ') && !disabled) {
                fileInputRef.current?.click()
              }
            }}
          >
            <InboxOutlined className={styles.csvDropZoneIcon} />
            <span className={styles.csvDropZoneText}>
              Click or drag a CSV file here
            </span>
            <span className={styles.csvDropZoneHint}>
              Max {formatFileSize(maxFileSizeBytes)} · .csv only
            </span>
          </div>
        </Tooltip>
      )}
    </>
  )
}
